// Common cooking fractions sorted by denominator
const COOKING_FRACTIONS: Array<[number, number, string]> = [
  [1, 8, "1/8"],
  [1, 4, "1/4"],
  [1, 3, "1/3"],
  [3, 8, "3/8"],
  [1, 2, "1/2"],
  [5, 8, "5/8"],
  [2, 3, "2/3"],
  [3, 4, "3/4"],
  [7, 8, "7/8"],
];

const SNAP_TOLERANCE = 0.08;

export function parseAmount(amount: string): number | null {
  if (!amount || !amount.trim()) return null;
  const s = amount.trim();

  // Mixed number: "1 1/2" or "2 3/4"
  const mixed = s.match(/^(\d+)\s+(\d+)\/(\d+)$/);
  if (mixed) {
    const den = parseInt(mixed[3]);
    if (den === 0) return null;
    return parseInt(mixed[1]) + parseInt(mixed[2]) / den;
  }

  // Simple fraction: "1/2"
  const frac = s.match(/^(\d+)\/(\d+)$/);
  if (frac) {
    const den = parseInt(frac[2]);
    if (den === 0) return null;
    return parseInt(frac[1]) / den;
  }

  // Decimal or whole number
  const num = parseFloat(s);
  return isNaN(num) ? null : num;
}

export function formatAmount(value: number): string {
  if (value <= 0) return "0";

  const whole = Math.floor(value);
  const frac = value - whole;

  if (frac < SNAP_TOLERANCE) {
    return whole === 0 ? "0" : String(whole);
  }

  if (1 - frac < SNAP_TOLERANCE) {
    return String(whole + 1);
  }

  // Find nearest common cooking fraction
  let bestLabel = "";
  let bestDist = Infinity;
  for (const [num, den, label] of COOKING_FRACTIONS) {
    const d = Math.abs(frac - num / den);
    if (d < bestDist) {
      bestDist = d;
      bestLabel = label;
    }
  }

  if (bestDist <= SNAP_TOLERANCE && bestLabel) {
    return whole > 0 ? `${whole} ${bestLabel}` : bestLabel;
  }

  // Fallback: one decimal place, strip trailing .0
  const rounded = Math.round(value * 10) / 10;
  return rounded % 1 === 0 ? String(Math.round(rounded)) : rounded.toFixed(1);
}

export function scaleAmount(
  amount: string | null | undefined,
  factor: number
): string | null {
  if (!amount || !amount.trim()) return amount ?? null;
  if (factor === 1) return amount;

  const s = amount.trim();

  // Range: "1-2" or "1 - 2"
  const rangeMatch = s.match(/^(.+?)\s*[-\u2013]\s*(.+)$/);
  if (rangeMatch) {
    const lo = parseAmount(rangeMatch[1]);
    const hi = parseAmount(rangeMatch[2]);
    if (lo !== null && hi !== null) {
      return `${formatAmount(lo * factor)}-${formatAmount(hi * factor)}`;
    }
  }

  const val = parseAmount(s);
  if (val === null) return amount;
  return formatAmount(val * factor);
}
